import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useFetcher,
  ActionFunctionArgs,
  useParams,
  redirect,
} from 'react-router-dom';
import { Button } from '@mosey/components/buttons/Button';
import { SectionHeading, Confirmation } from '../components';
import { api } from '../utils/fetchApi';
import { AddressIFormValues } from '../types/intake';
import { DateField, NonUSAddress } from '../components/forms';
import { CountryCode } from '../constants';
import { cleanIntakeDates } from '../utils/intake';

async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);

  const addresses = JSON.parse(data.addresses as string);

  try {
    await api({
      url: `/api/legal_entity/register_employee_addresses`,
      method: 'POST',
      body: {
        addresses: [addresses],
      },
    });

    return {
      success: true,
      message:
        "Thank you – we'll use these addresses to register for the required PA Earned Income Tax (EIT) and Local Services Tax (LST) account(s).",
    };
  } catch (_error) {
    return { error: 'Failed to register employee addresses' };
  }
}

export type RegisterEmployeeFormValues = {
  start_date: Date | null;
  work_address: AddressIFormValues | null;
  personal_address: AddressIFormValues | null;
};

const RegisterEmployeeAddressPAFormView = () => {
  const { locationId } = useParams();
  if (locationId !== 'pa') {
    throw redirect(`/locations/usa/${locationId}/`);
  }
  const fetcher = useFetcher();
  const formMethods = useForm<RegisterEmployeeFormValues>({
    mode: 'onBlur',
    defaultValues: {
      start_date: null,
      work_address: {
        address_line_1: null,
        address_line_2: null,
        city: null,
        state: null,
        postal_code: null,
        country: CountryCode.US,
      },
      personal_address: {
        address_line_1: null,
        address_line_2: null,
        city: null,
        state: null,
        postal_code: null,
        country: CountryCode.US,
      },
    },
  });

  const {
    handleSubmit,
    clearErrors,
    reset,
    formState: { isDirty, isValid, errors },
  } = formMethods;

  const isLoading = fetcher.state === 'submitting';
  const hasSubmitError = fetcher.data?.error;
  const hasSubmitSuccess = fetcher.data?.success;
  const successMessage = fetcher.data?.message;

  const onCancel = () => {
    reset();
  };

  const onSubmit = async (formData: RegisterEmployeeFormValues) => {
    if (!isValid) return;

    const cleanedData = cleanIntakeDates(formData);

    const addressesData = {
      start_date: cleanedData.start_date,
      personal_address: cleanedData.personal_address,
      work_address: cleanedData.work_address,
    };

    fetcher.submit(
      { addresses: JSON.stringify(addressesData) },
      { method: 'POST' },
    );
  };

  useEffect(() => {
    if (!isDirty) return;

    // Reset validation messages if needed when the user touches form inputs
    if (hasSubmitError) {
      clearErrors();
    }
  }, [isDirty, hasSubmitError, clearErrors]);

  if (hasSubmitSuccess) {
    return (
      <Confirmation
        heading="Success"
        description={successMessage}
        buttonText="Back to home"
        onButtonClick={() => (window.location.href = '/home')}
      />
    );
  }

  return (
    <div className="flex h-full grow flex-col overflow-auto">
      <div className="p-16">
        <SectionHeading
          className="flex-1 pb-4"
          text="Register Employee in Pennsylvania"
        />

        {hasSubmitError && (
          <p className="mb-4 rounded-md bg-red-50 pl-7 pr-4 text-sm font-medium text-red-800">
            {hasSubmitError}
          </p>
        )}

        <div className="relative flex grow flex-col overflow-auto py-9 xl:max-w-xl">
          <section>
            <FormProvider {...formMethods}>
              <form>
                <DateField
                  name="start_date"
                  label="Start Date"
                  error={errors.start_date}
                  reactFormConfig={{
                    required: 'This is required',
                  }}
                />
                <div className="mt-8">
                  <div>
                    <NonUSAddress
                      label="Work Address"
                      description="Work address for the employee."
                      error={errors.work_address}
                      name="work_address"
                      defaultCountry={CountryCode.US}
                    />
                  </div>
                </div>
                <div className="mt-8">
                  <div>
                    <NonUSAddress
                      label="Home Address"
                      description="Home address for the employee."
                      error={errors.personal_address}
                      name="personal_address"
                      defaultCountry={CountryCode.US}
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
          </section>
        </div>
      </div>
      {isDirty && (
        <div className="sticky bottom-0 left-0 flex h-[72px] items-center justify-end space-x-2 border-t border-gray-200 bg-sage-50 px-6 py-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <div className="w-28">
            <Button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              isFullWidth
              isLoading={isLoading}
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

RegisterEmployeeAddressPAFormView.action = action;

export default RegisterEmployeeAddressPAFormView;
